/**
 * Engine Editor operational-isolated composition shell — inline cell editor
 * per scope §5.5.2 amended + Sol direction at Day 2.5 P0 close (Polaris-inspired
 * inline edit for engine-isolated + operational + clean-reversible fields).
 *
 * Day 2.5 P2 scope: wires four bswc fields per scope §5.5.2 amended V1 4a field
 * list (tooltip_text, label, sort_order, notes).
 *
 * Day 2.5 P4 tooltip preview overlay (tooltip_text only): renders current tooltip
 * preview inline via resolveTooltip pure function (Day 91a.5 P2D bswcTooltipFallback)
 * per scope §7.4 — Engine Editor becomes consumer 3 → Template 18 promotion trigger
 * fires.
 *
 * Affordance shape per §5.5.2 amended (L5+L6+L9):
 *   - Inline save on blur / Enter; Escape cancels (no confirmation prompt)
 *   - Optional annotation slot collapsed by default; admin discretion
 *   - No propagation preview / notification / residue (no engine cascade)
 *   - Immediate reversibility framing at save ("This edit takes effect
 *     immediately. Prior text not retained.") per clean-reversible framing
 */

import { useEffect, useRef, useState, type JSX } from 'react';
import { supabase } from '../../../lib/supabase';
import { resolveTooltip } from '../../../../../../src/lib/bswcTooltipFallback';
import type { BuyScoreWeightComponent } from '../../../../../../src/types';

export type OperationalIsolatedField = 'tooltip_text' | 'label' | 'sort_order' | 'notes';

export interface EngineEditorOperationalIsolatedCellProps {
  rowId: string;
  field: OperationalIsolatedField;
  currentValue: string | number | null;
  /**
   * All bswc rows in route-altitude state. Required only for `tooltip_text`
   * field (P4 tooltip preview consumes resolveTooltip). Other fields ignore.
   */
  allRows?: BuyScoreWeightComponent[];
  /** bswc.key of the row being edited — required for tooltip preview resolution. */
  rowKey?: string;
  onSaved: () => void;
  onCancel: () => void;
}

export default function EngineEditorOperationalIsolatedCell({
  rowId,
  field,
  currentValue,
  allRows,
  rowKey,
  onSaved,
  onCancel,
}: EngineEditorOperationalIsolatedCellProps): JSX.Element {
  // Initial value coerced to string for input; sort_order parsed on save.
  const [value, setValue] = useState<string>(() => {
    if (currentValue === null || currentValue === undefined) return '';
    return String(currentValue);
  });
  const [annotationOpen, setAnnotationOpen] = useState<boolean>(false);
  const [annotation, setAnnotation] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Auto-focus input on mount per Polaris inline-edit pattern.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Day 2.5 P4 tooltip preview consumption per scope §7.4 — Engine Editor
  // becomes consumer 3 of resolveTooltip; Template 18 promotion trigger fires
  // at first call site on this build day. Preview updates live as admin types
  // new tooltip_text (Polaris-inspired immediate-feedback pattern; reduces
  // cognitive surprise).
  let tooltipPreview: string | undefined;
  if (field === 'tooltip_text' && allRows && rowKey) {
    // Mirror the row with the proposed new tooltip_text value so resolveTooltip
    // computes preview from the in-progress edit, not the committed substrate.
    const previewRows = allRows.map((r) =>
      r.key === rowKey ? { ...r, tooltip_text: value || null } : r,
    );
    tooltipPreview = resolveTooltip(previewRows, rowKey);
  }

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);

    // Per-field branching for type-narrow supabase update (RejectExcessProperties
    // strict typing requires concrete column key, not dynamic key). label is
    // NOT NULL per `supabase/schema.sql:7798`; tooltip_text + notes + applies_to_metric
    // are nullable.
    let updateError: { message: string } | null = null;
    if (field === 'sort_order') {
      const parsed = parseInt(value, 10);
      if (Number.isNaN(parsed)) {
        setError('sort_order must be an integer');
        setSaving(false);
        return;
      }
      const res = await supabase
        .from('buy_score_weight_components')
        .update({ sort_order: parsed })
        .eq('id', rowId);
      updateError = res.error;
    } else if (field === 'label') {
      if (value === '') {
        setError('label cannot be empty');
        setSaving(false);
        return;
      }
      const res = await supabase
        .from('buy_score_weight_components')
        .update({ label: value })
        .eq('id', rowId);
      updateError = res.error;
    } else if (field === 'tooltip_text') {
      const res = await supabase
        .from('buy_score_weight_components')
        .update({ tooltip_text: value === '' ? null : value })
        .eq('id', rowId);
      updateError = res.error;
    } else if (field === 'notes') {
      const res = await supabase
        .from('buy_score_weight_components')
        .update({ notes: value === '' ? null : value })
        .eq('id', rowId);
      updateError = res.error;
    }

    if (updateError) {
      setError(updateError.message);
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
  };

  // Keyboard handlers per Polaris inline-edit pattern: Enter saves, Escape cancels.
  // Use shift+Enter for newline in textarea (notes field).
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!saving) onCancel();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && field !== 'notes') {
      e.preventDefault();
      void handleSave();
    }
  };

  // notes field uses textarea for multi-line input; rest use single-line input.
  const useTextarea = field === 'notes';

  return (
    <div className="space-y-1.5 min-w-[180px]">
      {/* Inline editor input */}
      {useTextarea ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => !saving && void handleSave()}
          onKeyDown={handleKeyDown}
          disabled={saving}
          rows={2}
          className="w-full px-1.5 py-0.5 text-xs bg-surface-2 border border-accent rounded text-ink-primary focus:outline-none resize-y"
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={field === 'sort_order' ? 'number' : 'text'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => !saving && void handleSave()}
          onKeyDown={handleKeyDown}
          disabled={saving}
          className="w-full px-1.5 py-0.5 text-xs bg-surface-2 border border-accent rounded text-ink-primary focus:outline-none"
        />
      )}

      {/* Day 2.5 P4 tooltip preview (tooltip_text field only) — consumer 3 per
          scope §7.4 → Template 18 promotion trigger fires here. */}
      {field === 'tooltip_text' && (
        <p className="text-xs text-ink-muted italic">
          Preview: {tooltipPreview ? `"${tooltipPreview}"` : '(no tooltip resolved)'}
        </p>
      )}

      {/* Optional annotation slot — collapsed by default */}
      {!annotationOpen ? (
        <button
          type="button"
          onClick={() => setAnnotationOpen(true)}
          disabled={saving}
          className="text-2xs text-ink-muted hover:text-accent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Add rationale (optional)
        </button>
      ) : (
        <textarea
          value={annotation}
          onChange={(e) => setAnnotation(e.target.value)}
          disabled={saving}
          rows={1}
          placeholder="Rationale (optional)"
          className="w-full px-1.5 py-0.5 text-2xs bg-surface-2 border border-border rounded text-ink-secondary focus:border-accent focus:outline-none resize-y"
        />
      )}

      {/* Immediate reversibility framing per §5.5.2 — appears below edit during input */}
      <p className="text-2xs text-ink-muted italic">
        Saves immediately on blur/Enter. Prior text not retained.
      </p>

      {error && <p className="text-2xs text-red-500">Save failed: {error}</p>}

      {saving && <p className="text-2xs text-ink-muted">Saving…</p>}
    </div>
  );
}
